// Shared, pure safe-harbor detection used by both the calc engine's compliance
// screens and Julio's goal optimizer, so the two never drift on what "counts" as
// a safe harbor. A safe harbor changes what the informal ADP/ACP screens mean: a
// qualifying design deems the ADP test satisfied (and a qualifying MATCH also
// deems the match portion of the ACP test satisfied), so the deferral-based
// screen no longer gates the design.
//
// These are MODELED determinations from the plan settings only. They cannot see
// the plan document, a timely safe-harbor notice, the actual match formula's
// non-increasing-rate / 6%-cap details beyond the tiers entered, or a QACA's
// vesting/auto-enrollment terms. They never represent a formal determination.

// The minimum safe-harbor nonelective rate that deems the ADP test satisfied.
export const SAFE_HARBOR_NEC_MIN_PERCENT = 3

// A match formula at least as generous as the statutory safe harbor. We accept
// the basic formula (100% to 3%, then 50% on the next 2%) and any single-tier
// enhanced match of 100% on at least the first 4%. Anything less generous is not
// treated as a safe harbor.
export function isSafeHarborMatch(tiers) {
  if (!Array.isArray(tiers) || !tiers.length) return false
  const sorted = [...tiers].sort((a, b) => (a.upToPercent || 0) - (b.upToPercent || 0))
  const first = sorted[0]
  // Enhanced: a single 100% tier covering at least the first 4%.
  if (sorted.length === 1) {
    return Number(first.matchPercent) >= 100 && Number(first.upToPercent) >= 4
  }
  // Basic: 100% up to 3%, then 50% up to 5% (or more generous).
  const second = sorted[1]
  return (
    Number(first.matchPercent) >= 100 && Number(first.upToPercent) >= 3 &&
    Number(second.matchPercent) >= 50 && Number(second.upToPercent) >= 5
  )
}

// Classify an employer-contribution design's safe-harbor status and what it deems
// satisfied. `deemsAdp` → the ADP test is deemed met; `deemsAcpMatch` → the MATCH
// portion of the ACP test is deemed met (a safe-harbor NONELECTIVE does not, on
// its own, satisfy ACP, and employee after-tax contributions are always ACP-
// tested regardless of safe-harbor status).
export function evaluateSafeHarbor(ec = {}) {
  const isNonelective = Boolean(
    ec.necEnabled && ec.necIsSafeHarbor && Number(ec.necPercent) >= SAFE_HARBOR_NEC_MIN_PERCENT,
  )
  const isMatch = Boolean(ec.matchEnabled && isSafeHarborMatch(ec.matchTiers))
  const type = isMatch && isNonelective
    ? 'both'
    : isMatch ? 'match' : isNonelective ? 'nonelective' : 'none'
  return {
    type,
    isMatch,
    isNonelective,
    deemsAdp: isMatch || isNonelective,
    deemsAcpMatch: isMatch,
  }
}

// A human label for the safe-harbor status, used in UI/report copy.
export function safeHarborLabel(type) {
  if (type === 'both') return 'Safe harbor (match + nonelective)'
  if (type === 'match') return 'Safe harbor match'
  if (type === 'nonelective') return 'Safe harbor nonelective'
  return 'Not a safe harbor'
}
